import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Star, MapPin, Crown, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { CATEGORY_LABELS } from "../components/FeedCard";
import SafeImage from "../components/SafeImage";
import BannerAd from "../components/BannerAd";
import { planBannerSlots } from "../lib/bannerSlots";
import AiPhotoBadge from "../components/AiPhotoBadge";
import SEO from "../components/SEO";
import VerticalShortcuts from "../components/VerticalShortcuts";
import { canonicalForCategory } from "../lib/seo";
import { logInternalSearch } from "../lib/analytics";

const CATS = ["all", "onde-comer", "hoteis", "empresas", "saude", "imoveis"];

const PLAN_BADGE = {
  premium: { label: "Premium", cls: "bg-primary text-primary-foreground" },
  pro: { label: "Pro", cls: "bg-[hsl(213_90%_55%)] text-white" },
  business: { label: "Business", cls: "bg-success text-white" },
};

export default function Businesses() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [cat, setCat] = useState(searchParams.get("category") || "all");
  const [seed] = useState(() => Math.random().toString(36).slice(2, 10));
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const loaderRef = useRef(null);
  const LIMIT = 24;

  const [banners, setBanners] = useState([]);
  useEffect(() => {
    api.get("/banners", { params: { placement: "businesses" } }).then(({ data }) => setBanners(data.items || [])).catch(() => {});
  }, []);

  const fetchPage = useCallback(async (reset = false) => {
    if (loading) return;
    setLoading(true);
    const currentSkip = reset ? 0 : skip;
    try {
      const { data } = await api.get("/businesses", { params: { category: cat, q: q || undefined, skip: currentSkip, limit: LIMIT, seed } });
      setItems((prev) => {
        if (reset) return data.items;
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...data.items.filter((n) => !seen.has(n.id))];
      });
      setSkip(currentSkip + data.items.length);
      setHasMore(data.has_more);
      setTotal(data.total);
    } catch { setHasMore(false); }
    finally { setLoading(false); }
  }, [cat, q, skip, loading]);

  useEffect(() => {
    const t = setTimeout(() => { setItems([]); setSkip(0); setHasMore(true); fetchPage(true); }, q ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat, q]);

  // Log real internal searches (with the current total after the response is set).
  const lastLogRef = useRef("");
  useEffect(() => {
    if (!q || q.trim().length < 2 || loading) return;
    if (lastLogRef.current === q) return;
    lastLogRef.current = q;
    logInternalSearch({ query: q, source: "businesses", category: cat === "all" ? null : cat, results_count: total });
  }, [q, cat, total, loading]);

  useEffect(() => {
    const el = loaderRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading) fetchPage(false);
    }, { rootMargin: "400px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, fetchPage]);

  const bannerSlots = React.useMemo(() => {
    const out = {};
    const seedNum = parseInt(seed, 36) || 1;
    Object.entries(planBannerSlots(items.length, banners, seedNum)).forEach(([k, bn]) => {
      const n = Number(k) % 2 === 0 ? Number(k) + 1 : Number(k);
      if (n < items.length - 1) out[n] = bn;
    });
    return out;
  }, [items.length, banners, seed]);

  return (
    <div className="space-y-5">
      <SEO title="Empresas em João Pessoa"
        description="Descubra restaurantes, hotéis, clínicas e serviços de João Pessoa/PB. Empresas verificadas com contato, fotos e cupons."
        path="/empresas"
        canonicalPath={canonicalForCategory(cat, "/empresas")}
        noindex={!!q}
        keywords="empresas João Pessoa, restaurantes, hotéis, serviços, Paraíba" />
      <div>
        <h1 className="font-heading font-bold text-2xl">Empresas</h1>
        <p className="text-sm text-muted-foreground">{total.toLocaleString("pt-BR")} negócios de João Pessoa</p>
      </div>

      <VerticalShortcuts />

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <input data-testid="business-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar empresa..."
          className="w-full h-12 pl-12 pr-4 rounded-full bg-card border border-border focus:ring-2 focus:ring-primary outline-none" />
      </div>

      <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 md:flex-wrap md:overflow-visible md:pb-0">
        {CATS.map((c) => (
          <button key={c} data-testid={`business-cat-${c}`} onClick={() => setCat(c)}
            className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors active:scale-95 ${
              cat === c ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"
            }`}>
            {c === "all" ? "Todas" : CATEGORY_LABELS[c] || c}
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((b, i) => {
          const badge = PLAN_BADGE[b.plan];
          return (
            <React.Fragment key={b.id}>
              <motion.button
                data-testid={`business-card-${b.id}`}
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min((i % LIMIT) * 0.03, 0.3) }}
                onClick={() => navigate(`/empresa/${b.slug}`)}
                className="text-left rounded-3xl overflow-hidden border border-border bg-card soft-shadow hover:-translate-y-1 transition-transform"
              >
              <div className="relative h-36">
                {b.cover ? (
                  <SafeImage src={b.cover} category={b.category} alt={b.name} loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary flex items-center justify-center">
                    <span className="font-heading font-extrabold text-3xl text-primary">{b.name?.[0]?.toUpperCase()}</span>
                  </div>
                )}
                {b.cover && b.ai_image && <AiPhotoBadge />}
                {b.featured && (
                  <span data-testid={`business-featured-${b.id}`} className="absolute top-3 left-3 flex items-center gap-1 rounded-full bg-primary text-primary-foreground text-[11px] font-bold px-2.5 py-1"><Star className="w-3 h-3 fill-current" /> Destaque</span>
                )}
                {!b.featured && b.instagram && (
                  <span data-testid={`business-social-${b.id}`} className="absolute top-3 left-3 rounded-full bg-black/55 backdrop-blur-sm text-white text-[11px] font-semibold px-2.5 py-1">Nas redes</span>
                )}
                {badge && (
                  <span className={`absolute top-3 right-3 flex items-center gap-1 rounded-full text-[11px] font-bold px-2.5 py-1 ${badge.cls}`}>
                    {b.plan === "premium" && <Crown className="w-3 h-3" />}{badge.label}
                  </span>
                )}
              </div>
              <div className="p-4">
                <p className="font-heading font-bold leading-tight line-clamp-1">{b.name}</p>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-3 h-3 shrink-0" /><span className="line-clamp-1">{b.address}</span></p>
                <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                  <Star className="w-3.5 h-3.5 fill-alert text-alert" /> {(4.2 + (i % 8) * 0.1).toFixed(1)} · {CATEGORY_LABELS[b.category] || b.category}
                </div>
              </div>
              </motion.button>
              {bannerSlots[i] && <div className="sm:col-span-2"><BannerAd banner={bannerSlots[i]} /></div>}
            </React.Fragment>
          );
        })}
      </div>

      {items.length === 0 && !loading && (
        <div data-testid="business-empty" className="text-center py-16 text-muted-foreground">Nenhuma empresa encontrada.</div>
      )}

      <div ref={loaderRef} className="flex justify-center py-8">
        {loading && <Loader2 className="w-6 h-6 animate-spin text-primary" data-testid="business-loading" />}
        {!hasMore && items.length > 0 && <p className="text-sm text-muted-foreground">Você viu todas as {total.toLocaleString("pt-BR")} empresas ✨</p>}
      </div>
    </div>
  );
}
